import React, { forwardRef } from 'react';

type Variant = 'default' | 'hoverable' | 'interactive';
type Padding = 'none' | 'sm' | 'md' | 'lg';

interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: Variant;
  padding?: Padding;
  as?: 'div' | 'section' | 'article' | 'button' | 'a';
}

const VARIANT: Record<Variant, string> = {
  default:     'border border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900',
  hoverable:   'border border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900 hover:border-primary/30 hover:shadow-xl hover:shadow-primary/5 transition-all',
  interactive: 'border border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900 hover:border-primary/40 hover:shadow-xl hover:shadow-primary/10 transition-all cursor-pointer',
};

const PADDING: Record<Padding, string> = {
  none: '',
  sm:   'p-4',
  md:   'p-5 sm:p-6',
  lg:   'p-6 sm:p-8',
};

export const Card = forwardRef<HTMLDivElement, CardProps>(
  ({ variant = 'default', padding = 'md', as = 'div', className = '', children, ...rest }, ref) => {
    const Tag = as as any;
    return (
      <Tag
        ref={ref}
        className={[
          'rounded-2xl',
          VARIANT[variant],
          PADDING[padding],
          className,
        ].filter(Boolean).join(' ')}
        {...rest}
      >
        {children}
      </Tag>
    );
  }
);
Card.displayName = 'Card';

// Sub-parts for structured cards with header/body/footer
export const CardHeader: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({ className = '', children, ...rest }) => (
  <div className={`flex items-center justify-between pb-4 border-b border-slate-100 dark:border-slate-800 ${className}`} {...rest}>
    {children}
  </div>
);

export const CardTitle: React.FC<React.HTMLAttributes<HTMLHeadingElement>> = ({ className = '', children, ...rest }) => (
  <h3 className={`text-base font-bold text-slate-900 dark:text-white ${className}`} {...rest}>
    {children}
  </h3>
);

export const CardBody: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({ className = '', children, ...rest }) => (
  <div className={`py-4 ${className}`} {...rest}>
    {children}
  </div>
);

export const CardFooter: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({ className = '', children, ...rest }) => (
  <div className={`flex items-center justify-end gap-3 pt-4 border-t border-slate-100 dark:border-slate-800 ${className}`} {...rest}>
    {children}
  </div>
);
